import { useRef, useEffect, useState, useCallback, useMemo, memo } from "react";
import type { BlipV2 } from "@/types/home";
import { VideoControlsOverlay } from "../home/VideoControlsOverlay";
import VideoPlayerV2 from "@/components/blips/VideoPlayerV2";

interface ProductCardProps {
  product: BlipV2;
  onClick?: () => void;
}

// Production mode check - disable logs in production for better performance
const isDev = process.env.NODE_ENV === "development";
const log = isDev ? console.log : () => { };
const logError = isDev ? console.error : () => { };

// Module-level tracking for currently active video across all BlipsCard instances
// This ensures only one video plays at a time on mobile/tablet
let activeVideoRef: HTMLVideoElement | null = null;

// Flag to prevent touch handlers from interfering when play button is clicked
// This prevents the video from pausing immediately after play button click
let isPlayButtonClick = false;
let playButtonClickVideo: HTMLVideoElement | null = null;

// Track when videos are played via touch to prevent play button from immediately pausing them
// Maps video element to timestamp when it was last played via touch
const touchPlayTimestamps = new Map<HTMLVideoElement, number>();
const TOUCH_PLAY_GRACE_PERIOD = 500; // 500ms grace period after touch play

// Optimized helper function to pause all videos except the specified one
// Batches DOM operations for better performance
const pauseAllVideosExcept = (exceptVideo: HTMLVideoElement | null) => {
  log("[pauseAllVideosExcept] Called", {
    exceptVideo: exceptVideo?.src?.substring(exceptVideo.src.length - 20),
    playButtonClickVideo: playButtonClickVideo?.src?.substring(playButtonClickVideo.src.length - 20),
    activeVideoRef: activeVideoRef?.src?.substring(activeVideoRef.src.length - 20),
  });

  // Use cached querySelectorAll result if available, otherwise query
  // This reduces DOM queries significantly
  const allContainers = document.querySelectorAll(
    '[data-blips-card-container="true"]'
  );

  // Batch DOM operations for better performance
  const videosToPause: HTMLVideoElement[] = [];

  allContainers.forEach((container) => {
    const video = container.querySelector("video") as HTMLVideoElement;
    if (video && video !== exceptVideo && video !== playButtonClickVideo) {
      videosToPause.push(video);
    }
  });

  // Batch pause operations
  videosToPause.forEach((video) => {
    video.pause();
    video.currentTime = 0;
  });

  // Update active video reference
  activeVideoRef = exceptVideo;
};

// Combined state object to reduce re-renders
interface VideoState {
  isDesktop: boolean;
  isInView: boolean;
  isHovered: boolean;
  isPlaying: boolean;
  isManuallyPaused: boolean;
  isMuted: boolean;
}

const BlipsCardComponent = ({ product, onClick }: ProductCardProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use single state object to batch updates and reduce re-renders
  const [state, setState] = useState<VideoState>({
    isDesktop: false,
    isInView: false,
    isHovered: false,
    isPlaying: false,
    isManuallyPaused: false,
    isMuted: true,
  });

  // Use refs for values that don't need to trigger re-renders
  const isDesktopRef = useRef(false);
  const isManuallyPausedRef = useRef(false);

  // Memoize product URL to prevent unnecessary re-renders
  const videoUrl = useMemo(() => product.blipUrl, [product.blipUrl]);
  const viewCount = useMemo(
    () => product?.blipEngagement?.viewCount?.toString(),
    [product?.blipEngagement?.viewCount]
  );
  const productName = useMemo(() => product?.productName, [product?.productName]);

  // State for video duration
  const [duration, setDuration] = useState<string>("0:00");

  // Optimized device detection with throttling
  useEffect(() => {
    // Use matchMedia for better performance than resize listener
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const updateDesktop = () => {
      const desktop = mediaQuery.matches;
      isDesktopRef.current = desktop;
      setState((prev) => ({ ...prev, isDesktop: desktop }));
    };

    // Initial check
    updateDesktop();

    // Use modern matchMedia listener instead of resize event (more performant)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateDesktop);
      return () => mediaQuery.removeEventListener("change", updateDesktop);
    } else {
      // Fallback for older browsers
      const handler = () => updateDesktop();
      window.addEventListener("resize", handler);
      return () => window.removeEventListener("resize", handler);
    }
  }, []);

  // Format video duration to MM:SS or H:MM:SS format
  const formatDuration = useCallback((seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return "0:00";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Get video duration when metadata is loaded
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(formatDuration(video.duration));
      }
    };

    // Check if duration is already available
    if (video.duration && isFinite(video.duration)) {
      setDuration(formatDuration(video.duration));
    }

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [videoUrl, formatDuration]);

  // Optimized mute state sync - only update when changed
  useEffect(() => {
    const video = videoRef.current;
    if (!video || video.muted === state.isMuted) return;
    video.muted = state.isMuted;
  }, [state.isMuted]);

  // Optimized video event handlers and intersection observer
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Memoized event handlers to prevent recreation
    const handlePlay = () => {
      log("[Video Event] play event fired", video.src?.substring(video.src.length - 20));
      setState((prev) => (prev.isPlaying ? prev : { ...prev, isPlaying: true }));
    };

    const handlePause = () => {
      log("[Video Event] pause event fired", video.src?.substring(video.src.length - 20));
      setState((prev) => (!prev.isPlaying ? prev : { ...prev, isPlaying: false }));
    };

    video.addEventListener("play", handlePlay, { passive: true });
    video.addEventListener("pause", handlePause, { passive: true });

    // Only set up autoplay observer for desktop devices
    if (!state.isDesktop) {
      return () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      };
    }

    // Optimized IntersectionObserver with requestIdleCallback for better performance
    const observerOptions = {
      threshold: 0.5,
      rootMargin: "50px", // Start loading slightly before visible
    };

    const observer = new IntersectionObserver(
      (entries) => {
        // Use requestAnimationFrame to batch DOM reads/writes
        requestAnimationFrame(() => {
          entries.forEach((entry) => {
            const isIntersecting = entry.isIntersecting;
            setState((prev) => {
              if (prev.isInView === isIntersecting) return prev;
              return { ...prev, isInView: isIntersecting };
            });

            if (isIntersecting && !isManuallyPausedRef.current) {
              video.play().catch((error) => {
                logError("Video play failed:", error);
              });
            } else if (!isIntersecting) {
              video.pause();
              video.currentTime = 0;
              isManuallyPausedRef.current = false;
              setState((prev) => ({ ...prev, isManuallyPaused: false }));
            }
          });
        });
      },
      observerOptions
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, [state.isDesktop]);

  // Optimized hover effect with debouncing for desktop only
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !state.isDesktop || !state.isInView || isManuallyPausedRef.current) return;

    // Use requestAnimationFrame for smooth hover transitions
    const rafId = requestAnimationFrame(() => {
      if (state.isHovered) {
        video.play().catch((error) => {
          logError("Video play failed:", error);
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [state.isHovered, state.isInView, state.isDesktop]);

  // Optimized touch handlers with throttling for mobile/tablet
  useEffect(() => {
    // Only set up touch handlers for mobile/tablet devices (not desktop)
    if (state.isDesktop) return;

    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    // Mark container for faster lookups
    if (!container.hasAttribute("data-blips-card-container")) {
      container.setAttribute("data-blips-card-container", "true");
    }

    // Optimized touch handler with early returns and batched operations
    const handleTouchStart = (e: TouchEvent) => {
      log("[handleTouchStart] Touch start on container", {
        video: video.src?.substring(video.src.length - 20),
        isPlayButtonClick,
        isManuallyPaused: isManuallyPausedRef.current,
        activeVideoRef: activeVideoRef?.src?.substring(activeVideoRef.src.length - 20),
      });

      // Early returns for better performance
      if (isPlayButtonClick || isManuallyPausedRef.current) {
        log("[handleTouchStart] Ignored - isPlayButtonClick or manually paused");
        return;
      }

      const touch = e.touches[0];
      if (!touch) return;

      // Batch operations using requestAnimationFrame
      requestAnimationFrame(() => {
        pauseAllVideosExcept(video);
        touchPlayTimestamps.set(video, Date.now());
        video.play().catch((error) => {
          logError("[handleTouchStart] Video play failed:", error);
        });
      });
    };

    // Optimized document touch handler with early returns and cached queries
    const handleDocumentTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const target = e.target as HTMLElement;

      log("[handleDocumentTouchStart] Document touch start", {
        target: target.tagName,
        isPlayButtonClick,
        activeVideoRef: activeVideoRef?.src?.substring(activeVideoRef.src.length - 20),
        playButtonClickVideo: playButtonClickVideo?.src?.substring(playButtonClickVideo.src.length - 20),
        thisVideo: video.src?.substring(video.src.length - 20),
      });

      // Early returns for performance
      if (isPlayButtonClick || !touch || activeVideoRef === playButtonClickVideo) {
        return;
      }

      // Fast path check for buttons
      if (
        target.closest('button[aria-label*="Play"]') ||
        target.closest('button[aria-label*="Pause"]')
      ) {
        return;
      }

      // Optimized container check - use parentElement traversal for better performance
      let isWithinAnyCard = false;
      let current: HTMLElement | null = target;

      // Traverse up the DOM tree instead of querySelectorAll
      while (current && !isWithinAnyCard) {
        if (current.hasAttribute("data-blips-card-container")) {
          isWithinAnyCard = true;
          break;
        }
        current = current.parentElement;
      }

      // Batch pause operation
      if (!isWithinAnyCard && activeVideoRef) {
        requestAnimationFrame(() => {
          pauseAllVideosExcept(null);
        });
      }
    };

    // Add optimized touch event listeners with passive flag
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchstart", handleDocumentTouchStart, {
      passive: true,
      capture: true,
    });

    // Cleanup event listeners
    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchstart", handleDocumentTouchStart, {
        capture: true,
      } as EventListenerOptions);

      if (activeVideoRef === video) {
        activeVideoRef = null;
      }
    };
  }, [state.isDesktop]);

  // Memoized play/pause handler with optimized logic
  const handlePlayPauseClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const video = videoRef.current;
    if (!video) return;

    log("[handlePlayPauseClick] Play button clicked", {
      video: video.src?.substring(video.src.length - 20),
      isPlaying: state.isPlaying,
      isDesktop: state.isDesktop,
      currentActiveVideo: activeVideoRef?.src?.substring(activeVideoRef.src.length - 20),
      isManuallyPaused: isManuallyPausedRef.current,
    });

    if (state.isPlaying) {
      const touchPlayTime = touchPlayTimestamps.get(video);
      const timeSinceTouchPlay = touchPlayTime ? Date.now() - touchPlayTime : Infinity;

      if (timeSinceTouchPlay < TOUCH_PLAY_GRACE_PERIOD && activeVideoRef === video) {
        touchPlayTimestamps.delete(video);
        return;
      }

      video.pause();
      isManuallyPausedRef.current = true;
      setState((prev) => ({ ...prev, isManuallyPaused: true }));

      if (activeVideoRef === video) activeVideoRef = null;
      if (playButtonClickVideo === video) {
        playButtonClickVideo = null;
        isPlayButtonClick = false;
      }
      touchPlayTimestamps.delete(video);
    } else {
      if (!state.isDesktop) {
        isPlayButtonClick = true;
        playButtonClickVideo = video;
        pauseAllVideosExcept(video);
        activeVideoRef = video;

        video.play()
          .then(() => {
            isManuallyPausedRef.current = false;
            setState((prev) => ({ ...prev, isManuallyPaused: false }));
          })
          .catch((error) => {
            logError("[handlePlayPauseClick] Video play failed:", error);
          });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              isPlayButtonClick = false;
              setTimeout(() => {
                playButtonClickVideo = null;
              }, 200);
            }, 100);
          });
        });
      } else {
        video.play().catch((error) => {
          logError("[handlePlayPauseClick] Video play failed:", error);
        });
        isManuallyPausedRef.current = false;
        setState((prev) => ({ ...prev, isManuallyPaused: false }));
      }
    }
  }, [state.isPlaying, state.isDesktop]);

  // Memoized card click handler
  const handleCardClick = useCallback(() => {
    if (onClick && !isManuallyPausedRef.current) {
      onClick();
    }
  }, [onClick]);

  // Memoized hover handlers
  const handleMouseEnter = useCallback(() => {
    if (state.isDesktop) {
      setState((prev) => ({ ...prev, isHovered: true }));
    }
  }, [state.isDesktop]);

  const handleMouseLeave = useCallback(() => {
    if (state.isDesktop) {
      setState((prev) => ({ ...prev, isHovered: false }));
    }
  }, [state.isDesktop]);

  // Memoized mute toggle
  const handleMuteToggle = useCallback(() => {
    setState((prev) => ({ ...prev, isMuted: !prev.isMuted }));
  }, []);

  return (
    <div
      className="w-full sm:flex-shrink-0 sm:max-w-[191px] cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={containerRef}
        className="relative w-full h-[279px] rounded-[5px] overflow-hidden mb-1 bg-gray-100"
      >
        <VideoPlayerV2
          ref={videoRef}
          src={videoUrl}
          className="w-full h-full"
          muted={state.isMuted}
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          controlsList="nodownload nofullscreen noremoteplayback"
        />
        <VideoControlsOverlay
          viewCount={viewCount}
          duration={duration}
          isPlaying={state.isPlaying}
          isMuted={state.isMuted}
          onPlayPauseClick={handlePlayPauseClick}
          onMuteToggle={handleMuteToggle}
        />
      </div>
      {
        productName && (
          <h3 className="font-lexend text-[13px] lg:text-[14px] font-normal leading-[19.6px] tracking-[-0.14px] capitalize text-black line-clamp-2">
            {productName}
          </h3>
        )
      }
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
// Only re-render if product or onClick changes
export const BlipsCard = memo(BlipsCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.product?._id === nextProps.product?._id &&
    prevProps.product?.blipUrl === nextProps.product?.blipUrl &&
    prevProps.product?.productName === nextProps.product?.productName &&
    prevProps.product.blipEngagement?.viewCount === nextProps.product.blipEngagement?.viewCount &&
    prevProps.onClick === nextProps.onClick
  );
});